import camelCase from 'lodash/camelCase'
import startCase from 'lodash/startCase'
import React, { useEffect, useState } from 'react'

import { CalculatorLocationFields } from '../components/calculator/CalculatorLocationFields'
import { Footer } from '../components/Footer'
import { Spinner } from '../components/Spinner'
import { TopLogo } from '../components/TopLogo'
import { useCalculatorForm } from '../hooks/useCalculatorForm'
import { months } from '../models/Months.model'
import { USStates } from '../models/USStates.model'
import { getObjectKeyFromValue } from '../utils'
import { getIncomeCalcErrorMessage } from '../utils/calculatorErrors'
import { calculateCompensation, Compensation } from '../workers/compensationCalc'

type CalculatorResultContext = {
  city: string
  startDay: number
  startMonth: number
  startYear: number
  state: USStates
}

const formatCalculatorResultContext = (context: CalculatorResultContext): string => {
  const stateKey = getObjectKeyFromValue(USStates, context.state)
  const stateName = stateKey ? startCase(camelCase(stateKey)) : String(context.state)
  const monthName = months[context.startMonth - 1]

  return `${context.city}, ${stateName} — ${monthName} ${context.startDay}, ${context.startYear}`
}

const GSACalc: React.FC = () => {
  const calculatorForm = useCalculatorForm()
  const [isLoading, setIsLoading] = useState(false)
  const [submitErrorMessage, setSubmitErrorMessage] = useState('')
  const [showCalculation, setShowCalculation] = useState(false)
  const [calculation, setCalculation] = useState<Compensation | undefined>()
  const [resultContext, setResultContext] = useState<CalculatorResultContext | undefined>()

  const { fieldValues, canSubmit, resolvedCity, validateLocationForm, incomeCalcService } = calculatorForm

  useEffect(() => {
    setShowCalculation(false)
    setCalculation(undefined)
    setResultContext(undefined)
  }, [
    fieldValues.state,
    fieldValues.startMonth,
    fieldValues.startDay,
    fieldValues.startYear,
    fieldValues.cityQuery,
    fieldValues.selectedCity,
  ])

  const submitCalculator = () => {
    setShowCalculation(false)
    setCalculation(undefined)
    setResultContext(undefined)
    setSubmitErrorMessage('')

    if (!validateLocationForm()) {
      const validationElements = document.getElementsByClassName('validation-error')
      if (validationElements.length > 0) {
        ;(validationElements[0] as HTMLElement)?.focus()
      }
      return
    }

    const city = resolvedCity || fieldValues.selectedCity
    if (!city) {
      return
    }

    setIsLoading(true)
    incomeCalcService
      .get({
        city,
        startDay: fieldValues.startDay,
        startMonth: fieldValues.startMonth,
        startYear: fieldValues.startYear,
        state: fieldValues.state,
      })
      .then((GSAResult) => {
        const newCompensation = calculateCompensation({
          GSAResult,
          hourAmount: 0,
          hourlyRate: 0,
          numberOfDays: 7,
          overtimeAmount: 0,
          overtimeRate: 0,
          startMonth: fieldValues.startMonth,
        })

        setResultContext({
          city,
          startDay: fieldValues.startDay,
          startMonth: fieldValues.startMonth,
          startYear: fieldValues.startYear,
          state: fieldValues.state,
        })
        setCalculation(newCompensation)
        setShowCalculation(true)
        setIsLoading(false)

        const focusTrapElement = document.getElementsByClassName('calculator-focus-trap')
        if (focusTrapElement.length > 0) {
          ;(focusTrapElement[0] as HTMLElement)?.focus()
        }
      })
      .catch((err) => {
        const { message, field } = getIncomeCalcErrorMessage(err)
        if (field) {
          calculatorForm.setValidationErrors((prev) => ({ ...prev, [field]: message }))
        } else {
          setSubmitErrorMessage(message)
        }
        setIsLoading(false)
        console.error(err)
      })
  }

  return (
    <div id="page-root" className="relative flex h-screen flex-col overflow-auto">
      <TopLogo />
      <div className="mx-auto w-full max-w-4xl flex-1 px-4 py-6 sm:py-8">
        <div>
          <div className="mb-8 text-center sm:mb-10">
            <h1 className="mb-2 text-2xl font-bold text-graphite-grey sm:text-3xl">GSA Calculator</h1>
            <p className="text-sm text-gray-600 sm:text-base">Get the most out of your contract</p>
          </div>

          <div className="mb-8 rounded-lg bg-white p-5 shadow-lg sm:p-6 md:p-8">
            <h2 className="mb-4 text-lg font-bold text-graphite-grey sm:text-xl">Enter Your Details</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
              <CalculatorLocationFields calculatorForm={calculatorForm} />
            </div>

            {submitErrorMessage.length > 0 && (
              <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
                {submitErrorMessage}
              </div>
            )}

            <button
              type="button"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-orange py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-8 sm:text-base"
              onClick={() => submitCalculator()}
              disabled={isLoading || !canSubmit}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>Calculating...</span>
                </>
              ) : (
                'Calculate'
              )}
            </button>
          </div>

          {showCalculation && calculation && resultContext && (
            <div className="mt-8">
              <div className="rounded-lg bg-aqua-mint p-5 shadow-lg sm:p-6 md:p-8">
                <h3 className="mb-2 text-lg font-bold text-white sm:text-xl">Expected Stipends Breakdown</h3>
                <p className="mb-5 text-sm text-white opacity-90 sm:mb-6">
                  GSA max rates for {formatCalculatorResultContext(resultContext)} are
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="rounded-lg border border-white border-opacity-30 bg-white bg-opacity-20 p-4 backdrop-blur-sm">
                    <div className="mb-1.5 text-xs text-white opacity-90 sm:text-sm">Lodging</div>
                    <div className="text-xl font-bold text-white sm:text-2xl">
                      {typeof calculation.expectedStipends.lodgingRatePerDay !== 'number'
                        ? `$${Number(calculation.expectedStipends.lodgingRatePerDay) * 7}`
                        : `$${calculation.expectedStipends.lodgingRatePerDay * 7}`}
                      <span className="ml-1 text-xs font-normal opacity-75 sm:text-sm">/ week</span>
                    </div>
                  </div>
                  <div className="rounded-lg border border-white border-opacity-30 bg-white bg-opacity-20 p-4 backdrop-blur-sm">
                    <div className="mb-1.5 text-xs text-white opacity-90 sm:text-sm">Meals</div>
                    <div className="text-xl font-bold text-white sm:text-2xl">
                      {typeof calculation.expectedStipends.mealsRatePerDay !== 'number'
                        ? `$${Number(calculation.expectedStipends.mealsRatePerDay) * 7}`
                        : `$${calculation.expectedStipends.mealsRatePerDay * 7}`}
                      <span className="ml-1 text-xs font-normal opacity-75 sm:text-sm">/ week</span>
                    </div>
                  </div>
                  <div className="rounded-lg border-2 border-white bg-white bg-opacity-30 p-4 backdrop-blur-sm">
                    <div className="mb-1.5 text-xs text-white opacity-90 sm:text-sm">Total Stipends</div>
                    <div className="text-2xl font-bold text-white sm:text-3xl">
                      {typeof calculation.expectedStipends.totalStipends !== 'number'
                        ? calculation.expectedStipends.totalStipends
                        : `$${calculation.expectedStipends.totalStipends}`}
                      <span className="ml-1 text-xs font-normal opacity-75 sm:text-sm">/ week</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="calculator-focus-trap" tabIndex={-1}></div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default GSACalc
